import {
  ApolloClient,
  ObservableSubscription,
  SubscriptionOptions,
  SubscriptionResult,
  useApolloClient,
} from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import {
  ReactElement,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { sleep } from '../../util/async';
import { logger } from '../../util/logger';

/* eslint-disable @typescript-eslint/no-explicit-any */
type ResultBuffer = any[];
interface ApolloSubscriptions {
  [subscriptionName: string]: ApolloSubscription;
}

interface ApolloSubscription {
  observable: ObservableSubscription | undefined;
  listeners: SubscriberListener[] | undefined;
  maxBufferSize?: number;
  resultBuffer?: ResultBuffer;
}

interface SubscriberListener {
  onResult: SubscribeOnDataCallback;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
type SubscribeOnDataCallback<T = any> = (data: T) => void;

type ApolloUnsubscribeCallback = () => void;

/* eslint-disable @typescript-eslint/no-explicit-any */
type ApolloSubscriptionOptions = SubscriptionOptions<
  Record<string, any>,
  any
> & {
  subscriptionName?: string;
  callbackOnly?: true;
  callback?: <T extends SubscriptionResult>(data: T) => void;
  resultBufferSize?: number;
  disablePreviousResultEmit?: boolean;
  disabled?: boolean;
  skip?: boolean;
};

const ApolloSubscriptionManagerContext = createContext<
  ApolloSubscriptionManager | undefined
>(undefined);

/* eslint-disable @typescript-eslint/no-explicit-any */
const subscriptionDefaultErrorHandler = (error: any) => {
  if (error.message !== 'Unauthorized') {
    logger.error('error in apollo subscription manager', {
      error,
    });
  } else {
    logger.warn('error in apollo subscription manager', {
      error,
    });
  }
};

class ApolloSubscriptionManager {
  protected subscriptions: ApolloSubscriptions = {};

  constructor(private client: ApolloClient<any>) {}

  public subscribe<T>(
    options: ApolloSubscriptionOptions,
    onResult: SubscribeOnDataCallback<T>
  ): ApolloUnsubscribeCallback | undefined {
    const definition = getMainDefinition(options.query);
    const subscriptionName =
      options.subscriptionName ?? definition?.name?.value;

    if (!subscriptionName) {
      throw new Error('no subscription name found');
    }

    if (options.disabled) {
      this.stop(subscriptionName);
      return () => {
        // empty callback
      };
    } else {
      return this.addSubscription(subscriptionName, options, {
        onResult,
      });
    }
  }

  protected addSubscription(
    subscriptionName: string,
    options: ApolloSubscriptionOptions,
    listener: SubscriberListener
  ): ApolloUnsubscribeCallback {
    logger.info('adding subscription', {
      subscriptionName,
    });

    const defaultSubscription: Partial<ApolloSubscription> = {
      maxBufferSize: options.resultBufferSize ?? 1,
      resultBuffer: [],
    };

    const subscription =
      this.subscriptions[subscriptionName] ?? defaultSubscription;

    const listeners: SubscriberListener[] = subscription?.listeners ?? [];
    let observable: ObservableSubscription | undefined =
      subscription?.observable;

    listeners.push(listener);

    if (
      subscription.observable === undefined ||
      subscription.observable.closed
    ) {
      logger.info('creating new observable for subscription', {
        subscriptionName,
      });

      observable = this.start(subscriptionName, options);
    } else {
      if (!options.disablePreviousResultEmit) {
        this.replayResultBuffer(listener, subscription.resultBuffer);
      }
    }

    this.subscriptions[subscriptionName] = {
      ...subscription,
      observable,
      listeners,
    };

    return () => {
      this.removeSubscription(subscriptionName, listener);
    };
  }

  protected notifySubscribers<T>(subscriptionName: string, result: T): void {
    const subscription = this.subscriptions[subscriptionName] ?? {};

    subscription?.listeners?.forEach((listener) => {
      listener.onResult(result);
    });

    if (subscription.resultBuffer === undefined) {
      subscription.resultBuffer = [];
    }

    const newBufferSize = subscription.resultBuffer.unshift(result);
    const maxBuggerSize = subscription.maxBufferSize ?? 1;

    if (newBufferSize > maxBuggerSize) {
      subscription.resultBuffer.splice(maxBuggerSize);
    }
  }

  protected removeSubscription(
    subscriptionName: string,
    listener: SubscriberListener
  ): void {
    logger.info('removing subscription', {
      subscriptionName,
    });

    const subscription = this.subscriptions[subscriptionName] ?? {};
    const listeners = (subscription?.listeners ?? []).filter(
      (l) => l !== listener
    );

    if (listeners.length <= 0) {
      logger.info('tearing down subscription. no more listeners', {
        subscriptionName,
      });

      this.stop(subscriptionName);
    }

    this.subscriptions[subscriptionName] = {
      ...subscription,
      listeners,
    };
  }

  protected start(
    subscriptionName: string,
    options: ApolloSubscriptionOptions
  ): ObservableSubscription {
    const observable = this.client.subscribe(options).subscribe({
      next: (result) =>
        this.notifySubscribers(subscriptionName, {
          data: result?.data,
        }),
      error: subscriptionDefaultErrorHandler,
    });

    return observable;
  }

  protected stop(subscriptionName: string): void {
    const subscription = this.subscriptions[subscriptionName] ?? {};

    if (!subscription.observable?.closed) {
      subscription.observable?.unsubscribe();
    }
  }

  protected async replayResultBuffer(
    listener: SubscriberListener,
    buffer?: ResultBuffer
  ): Promise<void> {
    if (buffer) {
      const bufferSize = buffer?.length ?? 0;

      // result buffer is newest to oldest so emit events in reverse order
      for (let i = bufferSize; i > 0; i--) {
        const result = buffer[i - 1];

        // introduce a small delay when replaying messages. this helps resolve rendering
        // issues when the results are emitted too fast
        await sleep(1);

        listener.onResult(result);
      }
    }
  }
}

interface ApolloSubscriptionProviderProps {
  children: ReactNode;
}

const ApolloSubscriptionManagerProvider = (
  props: ApolloSubscriptionProviderProps
): ReactElement | null => {
  const client = useApolloClient();
  const [manager, setManager] = useState<
    ApolloSubscriptionManager | undefined
  >();

  useEffect(() => {
    setManager(new ApolloSubscriptionManager(client));
  }, [client]);

  if (manager) {
    return (
      <ApolloSubscriptionManagerContext.Provider value={manager}>
        {props.children}
      </ApolloSubscriptionManagerContext.Provider>
    );
  }

  return null;
};

const useApolloSubscriptionManager = ():
  | ApolloSubscriptionManager
  | undefined => {
  return useContext(ApolloSubscriptionManagerContext);
};

function useApolloSharedSubscription<T extends SubscriptionResult>(
  options: ApolloSubscriptionOptions
): T {
  const manager = useApolloSubscriptionManager();
  const [result, setResult] = useState<T | undefined>();

  const stringified = JSON.stringify(options);

  const { callback, callbackOnly } = options;

  const onResultRef = useRef<SubscribeOnDataCallback | undefined>(undefined);

  const onResult = useCallback(
    (result: T) => {
      if (typeof callback === 'function') {
        callback(result);
      }

      if (!callbackOnly) {
        setResult(result);
      }
    },
    [callback, callbackOnly]
  );

  onResultRef.current = onResult;

  useEffect(() => {
    let unsubscribe: ApolloUnsubscribeCallback | undefined;

    if (manager && !options.skip) {
      unsubscribe = manager.subscribe<T>(options, (result: T) => {
        if (onResultRef.current) {
          onResultRef.current(result);
        }
      });
    }

    return () => {
      setResult(undefined);
      if (unsubscribe) {
        unsubscribe();
      }
    };
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [manager, stringified]);

  return result || ({ loading: false } as unknown as T);
}

export {
  ApolloSubscriptionManagerProvider,
  subscriptionDefaultErrorHandler,
  useApolloSharedSubscription,
  useApolloSubscriptionManager,
};
